import { useMemo } from "react";
import { Layers, MapPin, Info, X, Eye, EyeOff, Users, ChevronRight, ChevronDown, Plus, Upload, Trash2, Edit3 } from "lucide-react";
import { STATE_NAME, TERRITORIES } from "../data/broker";

/**
 * Compact sidebar panel controlling the network overlay on the map.
 *
 * The heavy visualization now lives on the map itself (choropleth +
 * per-state carrier detail card). This panel is a legend + TPA filter
 * chip list + contact-visibility toggle. Respects the sidebar-wide
 * Territory filter so what shows up here matches what's shaded on the map.
 */
export default function TpaNetworksPanel({
  tpaData,
  scopedTpaCoverage,
  activeTerritory,
  selectedTpa,
  onSelectTpa,
  focusedNetworkState,
  onSelectState,
  // TPA Directory
  tpaContacts,
  expandedTpaName,
  setExpandedTpaName,
  showTpaContactsOnMap,
  setShowTpaContactsOnMap,
  onAddTpaContact,
  onEditTpaContact,
  onDeleteTpaContact,
  onImportTpaContactsCsv,
}) {
  const { states, tpas, state_to_region: stateToRegion, loading, error } = tpaData || {};
  const coverageByState = scopedTpaCoverage || tpaData?.coverageByState || {};
  const territoryLabel =
    activeTerritory && activeTerritory !== "all"
      ? TERRITORIES[activeTerritory]?.label || activeTerritory
      : "All Territories";

  // Overall stats — respect the territory filter.
  const overall = useMemo(() => {
    const statesWithCoverage = Object.keys(coverageByState);
    const allCarriers = new Set();
    for (const s of statesWithCoverage) {
      const info = coverageByState[s];
      for (const c of info?.carriers || []) allCarriers.add(c);
    }
    return {
      states: statesWithCoverage.length,
      carriers: allCarriers.size,
    };
  }, [coverageByState]);

  // Per-TPA reach within the current territory scope.
  const tpaStats = useMemo(() => {
    const stats = new Map();
    for (const s of Object.keys(coverageByState)) {
      for (const row of coverageByState[s].tpas || []) {
        stats.set(row.tpa, (stats.get(row.tpa) || 0) + 1);
      }
    }
    return stats;
  }, [coverageByState]);

  // Only show TPAs that have at least one covered state in the current scope,
  // then keep the source order stable.
  const scopedTpas = useMemo(() => {
    const src = Array.isArray(tpas) ? tpas : [];
    if (activeTerritory === "all") return src.filter((t) => tpaStats.has(t));
    return src.filter((t) => tpaStats.has(t));
  }, [tpas, tpaStats, activeTerritory]);

  return (
    <div className="p-4 space-y-3" data-testid="networks-panel">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-emerald-300" />
        <h3 className="font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200">
          Network Overlay
        </h3>
      </div>

      <p className="font-mono text-[9.5px] leading-relaxed tracking-[0.05em] text-zinc-500">
        States on the map shade by carrier coverage. Pick a TPA to isolate
        its footprint. The sidebar Territory filter also narrows the
        overlay — currently showing <span className="text-emerald-200">{territoryLabel}</span>.
      </p>

      {loading && (
        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 py-2">
          Loading…
        </div>
      )}
      {error && (
        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-rose-300 py-2">
          {error}
        </div>
      )}

      {/* Overall stats (scope-aware) */}
      <div className="grid grid-cols-3 gap-2 border border-white/10 p-2 bg-[#0b0b0e]">
        <StatCell label="TPAs" value={scopedTpas.length} />
        <StatCell label="States" value={overall.states} />
        <StatCell label="Carriers" value={overall.carriers} />
      </div>

      {/* TPA chip filter */}
      <div className="space-y-1.5">
        <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500">
          // TPA Filter · {territoryLabel}
        </div>
        <button
          type="button"
          data-testid="tpa-chip-all"
          onClick={() => onSelectTpa && onSelectTpa("all")}
          className={`w-full flex items-center justify-between px-2 py-1.5 border font-mono text-[10.5px] tracking-[0.14em] uppercase transition-colors ${
            selectedTpa === "all"
              ? "bg-[#141418] border-emerald-400/70 text-emerald-100"
              : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
          }`}
        >
          <span>All TPAs</span>
          <span className="text-[9px] text-zinc-500 tabular-nums normal-case tracking-normal">
            {overall.states} states
          </span>
        </button>
        <div className="flex flex-col gap-1" data-testid="tpa-chip-list">
          {scopedTpas.length === 0 && (
            <div className="font-mono text-[10px] text-zinc-500 tracking-[0.1em] py-2">
              No TPA coverage in the selected territory.
            </div>
          )}
          {scopedTpas.map((tpa) => {
            const reach = tpaStats.get(tpa) || 0;
            const active = selectedTpa === tpa;
            return (
              <button
                key={tpa}
                type="button"
                onClick={() => onSelectTpa && onSelectTpa(tpa)}
                data-testid={`tpa-chip-${tpa.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                className={`flex items-center justify-between px-2 py-1.5 border font-mono text-[10.5px] tracking-[0.05em] transition-colors ${
                  active
                    ? "bg-[#141418] border-emerald-400/70 text-emerald-100"
                    : "border-white/10 text-zinc-300 hover:border-white/30 hover:text-zinc-100"
                }`}
              >
                <span className="truncate pr-2">{tpa}</span>
                <span className="text-[9px] text-zinc-500 tabular-nums whitespace-nowrap">
                  {reach} {reach === 1 ? "state" : "states"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Focused state summary */}
      {focusedNetworkState && (
        <div
          className="border border-emerald-400/40 bg-emerald-500/5 p-2.5 space-y-1"
          data-testid="focused-state-summary"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-emerald-300" />
              <div>
                <div className="font-mono text-[8.5px] tracking-[0.2em] uppercase text-emerald-200">
                  Focused
                </div>
                <div className="font-bold text-zinc-50 text-[12px]">
                  {STATE_NAME[focusedNetworkState] || focusedNetworkState}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onSelectState && onSelectState(null)}
              data-testid="focused-state-clear"
              className="text-zinc-500 hover:text-zinc-200"
              title="Clear focus"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <FocusedStateSummary
            info={tpaData?.coverageByState?.[focusedNetworkState]}
            marketTier={tpaData?.state_market_tiers?.[focusedNetworkState]}
            selectedTpa={selectedTpa}
          />
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-2 pt-2 border-t border-white/10">
        <Info className="w-3 h-3 text-zinc-500" />
        <span className="font-mono text-[8.5px] tracking-[0.18em] uppercase text-zinc-500">
          Darker green = broader coverage
        </span>
      </div>

      {/* Coverage gaps — states in the current territory scope without any
          confirmed TPA carrier data. */}
      <UncoveredStatesRow
        states={states}
        stateToRegion={stateToRegion}
        activeTerritory={activeTerritory}
        coverageByState={tpaData?.coverageByState}
      />

      {/* TPA Directory — points of contact at each TPA */}
      <TpaDirectory
        tpas={tpas}
        tpaContacts={tpaContacts}
        expandedTpaName={expandedTpaName}
        setExpandedTpaName={setExpandedTpaName}
        showTpaContactsOnMap={showTpaContactsOnMap}
        setShowTpaContactsOnMap={setShowTpaContactsOnMap}
        onAddTpaContact={onAddTpaContact}
        onEditTpaContact={onEditTpaContact}
        onDeleteTpaContact={onDeleteTpaContact}
        onImportTpaContactsCsv={onImportTpaContactsCsv}
      />

      <div className="font-mono text-[8.5px] tracking-[0.16em] uppercase text-zinc-600 pt-1 space-y-1">
        <div>
          Sources: State-by-State Coverage (SE·NE·MW·S) + Market Tier ranking · 2026-08
        </div>
        {tpaData?.market_tiers_disclaimer && (
          <div className="normal-case tracking-normal italic text-zinc-500">
            Market tiers: {tpaData.market_tiers_disclaimer}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="text-center">
      <div className="font-bold text-zinc-50 text-[16px] tabular-nums">{value}</div>
      <div className="font-mono text-[8.5px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">
        {label}
      </div>
    </div>
  );
}

function FocusedStateSummary({ info, marketTier, selectedTpa }) {
  const hasTier = marketTier && (marketTier.tier_1 || marketTier.tier_2 || marketTier.tier_3);
  const filtered = info?.tpas
    ? selectedTpa === "all"
      ? info.tpas
      : info.tpas.filter((t) => t.tpa === selectedTpa)
    : [];
  return (
    <div className="space-y-2 pt-1">
      {hasTier && (
        <div className="border border-amber-400/30 bg-amber-500/5 p-1.5">
          <div className="font-mono text-[8px] tracking-[0.2em] uppercase text-amber-200 mb-0.5">
            Market Tier · Directional
          </div>
          <div className="text-[10px] leading-tight text-zinc-200">
            <span className="text-amber-300 font-mono">T1</span> {marketTier.tier_1 || "—"}
            {marketTier.tier_2 && (
              <>
                {" · "}
                <span className="text-amber-300/80 font-mono">T2</span> {marketTier.tier_2}
              </>
            )}
            {marketTier.tier_3 && (
              <>
                {" · "}
                <span className="text-amber-300/60 font-mono">T3</span> {marketTier.tier_3}
              </>
            )}
          </div>
          {marketTier.independent_tpas && (
            <div className="text-[9.5px] text-zinc-400 mt-0.5">
              <span className="uppercase font-mono tracking-[0.14em] text-amber-200/70 text-[8.5px]">
                Indep TPAs
              </span>{" "}
              · {marketTier.independent_tpas}
            </div>
          )}
          {marketTier.notes && (
            <div className="text-[9.5px] text-zinc-500 italic mt-0.5">{marketTier.notes}</div>
          )}
        </div>
      )}
      {filtered.length === 0 ? (
        <div className="font-mono text-[10px] text-zinc-500">
          {info?.tpas
            ? `${selectedTpa} is not active in this state.`
            : "No confirmed TPA coverage."}
        </div>
      ) : (
        <div className="space-y-0.5">
          <div className="font-mono text-[8.5px] tracking-[0.2em] uppercase text-emerald-200/80">
            Virtue Health TPA Access
          </div>
          {filtered.map((row) => (
            <div key={row.tpa} className="text-[10px] leading-tight">
              <span className="font-mono uppercase tracking-[0.1em] text-emerald-200">
                {row.tpa}
              </span>{" "}
              <span className="text-zinc-400">→ {row.carriers.join(", ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UncoveredStatesRow({ states, stateToRegion, activeTerritory, coverageByState }) {  const uncovered = useMemo(() => {
    if (!states) return [];
    return states.filter((s) => {
      if (s === "NATIONAL") return false;
      if (activeTerritory !== "all" && stateToRegion && stateToRegion[s] !== activeTerritory) {
        return false;
      }
      return !(coverageByState && coverageByState[s]);
    });
  }, [states, stateToRegion, activeTerritory, coverageByState]);
  if (uncovered.length === 0) return null;
  return (
    <div className="border border-white/5 p-2 bg-[#0b0b0e]">
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-amber-200/70 mb-1">
        // Coverage Gap
      </div>
      <div className="flex flex-wrap gap-1">
        {uncovered.map((s) => (
          <span
            key={s}
            className="px-1.5 py-0.5 border border-amber-400/30 text-amber-100/80 text-[9px] font-mono tracking-[0.08em]"
            title={STATE_NAME[s] || s}
          >
            {s}
          </span>
        ))}
      </div>
      <div className="font-mono text-[8.5px] tracking-[0.14em] uppercase text-zinc-500 mt-1">
        {uncovered.length} state{uncovered.length === 1 ? "" : "s"} without confirmed TPA carriers
      </div>
    </div>
  );
}

function TpaDirectory({
  tpas,
  tpaContacts,
  expandedTpaName,
  setExpandedTpaName,
  showTpaContactsOnMap,
  setShowTpaContactsOnMap,
  onAddTpaContact,
  onEditTpaContact,
  onDeleteTpaContact,
  onImportTpaContactsCsv,
}) {
  // Bucket contacts by TPA name.
  const byTpa = useMemo(() => {
    const out = {};
    for (const c of tpaContacts || []) {
      const key = c.tpa_name || "";
      if (!out[key]) out[key] = [];
      out[key].push(c);
    }
    return out;
  }, [tpaContacts]);

  const total = (tpaContacts || []).length;

  return (
    <div
      className="border border-indigo-400/40 bg-indigo-500/5 p-2.5 space-y-2"
      data-testid="tpa-directory"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-indigo-300" />
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-indigo-200">
            TPA Directory
          </div>
        </div>
        <span className="font-mono text-[9px] tracking-[0.14em] uppercase text-indigo-200/60">
          {total} contact{total === 1 ? "" : "s"}
        </span>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2">
        <label
          className="flex-1 min-w-[120px] px-2 py-1 border border-white/10 hover:border-white/30 text-[10px] font-mono tracking-[0.14em] uppercase text-zinc-300 cursor-pointer flex items-center gap-1.5"
          data-testid="tpa-import-csv"
        >
          <Upload className="w-3 h-3" />
          Import CSV
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f && onImportTpaContactsCsv) onImportTpaContactsCsv(f);
              e.target.value = ""; // allow re-picking same file
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => setShowTpaContactsOnMap && setShowTpaContactsOnMap((v) => !v)}
          data-testid="tpa-toggle-map"
          className={`px-2 py-1 border font-mono text-[10px] tracking-[0.14em] uppercase transition-colors flex items-center gap-1.5 ${
            showTpaContactsOnMap
              ? "border-indigo-400/60 bg-indigo-500/10 text-indigo-100"
              : "border-white/10 text-zinc-400 hover:border-white/30"
          }`}
        >
          {showTpaContactsOnMap ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {showTpaContactsOnMap ? "On map" : "Off map"}
        </button>
      </div>

      {/* TPA rows */}
      <div className="space-y-0.5" data-testid="tpa-directory-list">
        {(tpas || []).map((t) => {
          const people = byTpa[t] || [];
          const isOpen = expandedTpaName === t;
          return (
            <div
              key={t}
              className="border border-white/10"
              data-testid={`tpa-dir-row-${t.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
            >
              <button
                type="button"
                onClick={() => setExpandedTpaName && setExpandedTpaName(isOpen ? null : t)}
                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-white/5"
              >
                <span className="flex items-center gap-1.5">
                  {isOpen ? (
                    <ChevronDown className="w-3 h-3 text-indigo-300" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-zinc-500" />
                  )}
                  <span className="font-mono text-[10.5px] tracking-[0.05em] text-zinc-200 truncate">
                    {t}
                  </span>
                </span>
                <span className="font-mono text-[9px] tracking-[0.14em] uppercase text-zinc-500">
                  {people.length} {people.length === 1 ? "person" : "people"}
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-white/10 p-2 space-y-1.5 bg-[#0b0b0e]">
                  {people.length === 0 ? (
                    <div className="font-mono text-[10px] text-zinc-500 tracking-[0.05em]">
                      No contacts yet.
                    </div>
                  ) : (
                    people.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-start justify-between gap-2 border border-white/5 p-1.5"
                        data-testid={`tpa-person-${p.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold text-zinc-100 truncate">
                            {p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "—"}
                          </div>
                          {p.lifecycle_stage && (
                            <div className="text-[9.5px] text-zinc-400 font-mono tracking-[0.05em]">
                              {p.lifecycle_stage}
                            </div>
                          )}
                          {p.email && (
                            <div className="text-[9.5px] text-indigo-200 truncate">
                              {p.email}
                            </div>
                          )}
                          {p.phone && (
                            <div className="text-[9.5px] text-zinc-500">{p.phone}</div>
                          )}
                          {p.note && (
                            <div className="text-[9.5px] text-zinc-500 italic mt-0.5 line-clamp-2">
                              {p.note}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => onEditTpaContact && onEditTpaContact(p.id)}
                            data-testid={`tpa-person-edit-${p.id}`}
                            className="p-1 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-zinc-100"
                            title="Edit"
                          >
                            <Edit3 className="w-2.5 h-2.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteTpaContact && onDeleteTpaContact(p.id)}
                            data-testid={`tpa-person-delete-${p.id}`}
                            className="p-1 border border-rose-500/30 hover:border-rose-400/60 text-rose-300"
                            title="Remove"
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={() => onAddTpaContact && onAddTpaContact(t)}
                    data-testid={`tpa-add-contact-${t.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                    className="w-full flex items-center justify-center gap-1 px-2 py-1 border border-dashed border-indigo-400/40 text-indigo-200 text-[10px] font-mono tracking-[0.16em] uppercase hover:bg-indigo-500/10"
                  >
                    <Plus className="w-3 h-3" /> Add contact at {t}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
